import type { Color } from 'three';
import type { Pcg32 } from '../../../sim/rng';
import type { SimEvent, World } from '../../../sim/types';
import type { BatchSet } from './batches';
import type { ScorchDecals } from './blast';
import type { LightPool } from './lights';
import type { ParticleSystem } from './particles';
import type { Shake } from './shake';

/** The payload of one kind of sim event. */
export type EventOf<T extends SimEvent['type']> = Extract<SimEvent, { type: T }>;

/** A man lying prone fires, and bleeds, from about here. */
export const PRONE_HEIGHT = 0.35;
/** Shoulder height of a LAW: where the rocket launches and flies. */
export const ROCKET_HEIGHT = 1.2;

/** Comfort switches (08 accessibility): both default to true, which is the look as designed. */
export interface EffectsToggles {
  /**
   * False for photosensitive players: muzzle and blast LIGHTS come up and die away gently at a
   * fifth of their strength instead of snapping on, fireballs skip their two white-hot frames,
   * and the muzzle star is smaller. Nothing is removed - every shot and blast can still be seen.
   */
  flashes: boolean;
  /** False: explosions no longer move the camera. */
  shake: boolean;
}

/**
 * What every effect handler works with: the shared services of one `Effects` instance.
 * Handlers are plain functions over this, so each concern lives in its own small module.
 */
export interface Fx {
  /** Presentation-only randomness: ONE stream, seeded, so look checks repeat. */
  readonly rng: Pcg32;
  readonly groundY: (x: number, z: number) => number;
  readonly propPosition: ((id: number) => { x: number; z: number } | null) | null;
  readonly set: BatchSet;
  readonly particles: ParticleSystem;
  readonly lights: LightPool;
  readonly shake: Shake;
  readonly scorch: ScorchDecals;
  readonly muzzleColor: Color;
  readonly blastColor: Color;
  /** Live: `Effects.setOptions` writes here and every handler reads it when it fires. */
  readonly toggles: EffectsToggles;
  /** The last world passed to `update`: events carry ids, not positions. */
  world: World | null;
  /** Interpolation factor of the frame being drawn. */
  alpha: number;
}
